import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  Loader2, Plus, Home, Calendar as CalendarIcon, DollarSign,
  CheckCircle, XCircle, Clock, MapPin, Users, Edit, Pause,
  Play, ChevronLeft, Building2, AlertTriangle, MessageSquare,
  Star, TrendingUp, Zap, Award, Eye, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  draft: 'bg-gray-100 text-gray-700',
  suspended: 'bg-red-100 text-red-700'
};

const BOOKING_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700',
  completed: 'bg-blue-100 text-blue-700'
};

const GRADE_COLORS = {
  A: 'text-green-600 bg-green-100',
  B: 'text-blue-600 bg-blue-100',
  C: 'text-yellow-600 bg-yellow-100',
  D: 'text-red-600 bg-red-100'
};

/**
 * Quality Score Badge Component
 */
const QualityScoreBadge = ({ score, grade }) => (
  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${GRADE_COLORS[grade] || GRADE_COLORS.D}`}>
    <Star className="h-3 w-3" />
    {score}/100
  </div>
);

/**
 * Listing Card Component
 */
const ListingCard = ({ listing, onStatusChange, t }) => {
  const photo = listing.photos?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400';
  
  const handleToggleStatus = async () => {
    const newStatus = listing.status === 'active' ? 'paused' : 'active';
    onStatusChange(listing.listing_id, newStatus);
  };
  
  return (
    <Card className="overflow-hidden">
      <div className="flex">
        <div className="w-32 h-32 flex-shrink-0 relative">
          <img src={photo} alt={listing.title} className="w-full h-full object-cover" />
          {listing.quality_score !== undefined && (
            <div className="absolute bottom-1 left-1">
              <QualityScoreBadge score={listing.quality_score} grade={listing.quality_grade} />
            </div>
          )}
        </div>
        <CardContent className="flex-1 p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold">{listing.title}</h3>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {listing.city}, {listing.country}
              </p>
            </div>
            <Badge className={STATUS_COLORS[listing.status] || STATUS_COLORS.draft}>
              {listing.status}
            </Badge>
          </div>
          
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
            <span>€{listing.nightly_price}/night</span>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {listing.max_occupants}
            </span>
            <span className="capitalize">{listing.accommodation_type?.replace('_', ' ')}</span>
          </div>
          
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" variant="outline" asChild>
              <Link to={`/host/listings/${listing.listing_id}/edit`}>
                <Edit className="h-3 w-3 mr-1" />
                Edit
              </Link>
            </Button>
            <Button size="sm" variant="outline" onClick={handleToggleStatus}>
              {listing.status === 'active' ? (
                <><Pause className="h-3 w-3 mr-1" /> Pause</>
              ) : (
                <><Play className="h-3 w-3 mr-1" /> Activate</>
              )}
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to={`/host/listings/${listing.listing_id}/availability`}>
                <CalendarIcon className="h-3 w-3 mr-1" />
                Calendar
              </Link>
            </Button>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

/**
 * Booking Card Component with Response Dialog
 */
const BookingCard = ({ booking, onRespond, t }) => {
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [responseAction, setResponseAction] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [checkInInstructions, setCheckInInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleRespond = async () => {
    if (!responseAction) return;
    
    setSubmitting(true);
    try {
      await onRespond(booking.booking_id, {
        action: responseAction,
        message: responseMessage || null,
        check_in_instructions: responseAction === 'confirm' ? checkInInstructions : null
      });
      setShowResponseDialog(false);
      toast.success(`Booking ${responseAction}ed successfully`);
    } catch (error) {
      toast.error('Failed to respond to booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card className="overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold">{booking.listing_title}</h4>
              <p className="text-sm text-gray-500">
                {booking.check_in} → {booking.check_out} ({booking.nights} nights)
              </p>
            </div>
            <Badge className={BOOKING_STATUS_COLORS[booking.status]}>
              {booking.status}
            </Badge>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
            <span className="font-medium">{booking.guest_name}</span>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {booking.guests}
            </span>
            <span className="font-medium text-green-600">€{booking.total}</span>
          </div>
          
          {booking.message && (
            <div className="bg-gray-50 rounded p-2 mb-3 text-sm">
              <span className="text-gray-500">Guest message: </span>
              <span className="italic">"{booking.message}"</span>
            </div>
          )}
          
          {booking.status === 'pending' && (
            <div className="flex gap-2">
              <Button 
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => {
                  setResponseAction('confirm');
                  setShowResponseDialog(true);
                }}
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                Confirm
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  setResponseAction('decline');
                  setShowResponseDialog(true);
                }}
              >
                <XCircle className="h-3 w-3 mr-1" />
                Decline
              </Button>
            </div>
          )}
          
          {booking.status !== 'pending' && booking.host_response && (
            <p className="text-sm text-gray-500 mt-2">
              Your response: "{booking.host_response}"
            </p>
          )}
        </CardContent>
      </Card>

      {/* Response Dialog */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {responseAction === 'confirm' ? 'Confirm Booking' : 'Decline Booking'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-medium">{booking.guest_name}</p>
              <p className="text-sm text-gray-500">
                {booking.check_in} → {booking.check_out} • {booking.guests} guests • €{booking.total}
              </p>
            </div>
            
            {responseAction === 'confirm' && (
              <div>
                <Label>Check-in Instructions (optional)</Label>
                <Textarea
                  value={checkInInstructions}
                  onChange={(e) => setCheckInInstructions(e.target.value)}
                  placeholder="Door code, key location, parking info..."
                  rows={3}
                />
              </div>
            )}
            
            <div>
              <Label>Message to Guest (optional)</Label>
              <Textarea
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder={responseAction === 'confirm' 
                  ? "Welcome message, directions, etc." 
                  : "Reason for declining..."
                }
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRespond}
              disabled={submitting}
              className={responseAction === 'confirm' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : responseAction === 'confirm' ? (
                <CheckCircle className="h-4 w-4 mr-2" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              {responseAction === 'confirm' ? 'Confirm Booking' : 'Decline Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Host Dashboard Main Component
 */
const HostDashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [dashboard, setDashboard] = useState(null);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notRegistered, setNotRegistered] = useState(false);
  
  // Get provider token from localStorage
  const token = localStorage.getItem('provider_token');

  useEffect(() => {
    if (!token) {
      navigate('/provider/login');
      return;
    }
    fetchHostData();
  }, [token]);

  const fetchHostData = async () => {
    setLoading(true);
    try {
      // Get full dashboard data
      const dashRes = await fetch(`${API_URL}/api/accommodation/host/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (dashRes.status === 404) {
        setNotRegistered(true);
        setLoading(false);
        return;
      }
      
      if (!dashRes.ok) throw new Error('Failed to load dashboard');
      
      const dashData = await dashRes.json();
      setDashboard(dashData);
      setListings(dashData.listings || []);

      // Get bookings
      const bookingsRes = await fetch(`${API_URL}/api/accommodation/host/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setBookings(bookingsData.bookings || []);
      }

    } catch (error) {
      console.error('Error fetching host data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAsHost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/accommodation/host/register`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Registration failed');
      }
      
      toast.success('Welcome! You are now registered as a host.');
      setNotRegistered(false);
      fetchHostData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStatusChange = async (listingId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/accommodation/host/listings/${listingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error('Failed to update listing');
      
      setListings(listings.map(l => 
        l.listing_id === listingId ? { ...l, status: newStatus } : l
      ));
      toast.success(`Listing ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch (error) {
      toast.error('Failed to update listing status');
    }
  };

  const handleBookingResponse = async (bookingId, response) => {
    const res = await fetch(`${API_URL}/api/accommodation/host/bookings/${bookingId}/respond`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(response)
    });
    
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Failed to respond');
    }
    
    // Refresh bookings
    fetchHostData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Not registered as host
  if (notRegistered) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Home className="h-8 w-8 text-purple-600" />
            </div>
            <CardTitle>{t('accommodation.host.register')}</CardTitle>
            <CardDescription>
              {t('accommodation.host.registerSubtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-green-700 font-medium">{t('accommodation.host.trial')}</p>
              <p className="text-sm text-green-600">{t('accommodation.host.trialDesc')}</p>
            </div>
            <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={handleRegisterAsHost}>
              {t('accommodation.host.register')}
            </Button>
            <Button variant="outline" className="w-full" asChild>
              <Link to="/provider/dashboard">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Provider Dashboard
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed');
  
  const stats = dashboard?.stats || {};
  const badges = dashboard?.badges || {};
  const subscription = dashboard?.subscription || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  {t('accommodation.host.title')}
                  {badges.fast_responder && (
                    <Badge className="bg-yellow-400 text-yellow-900">
                      <Zap className="h-3 w-3 mr-1" />
                      Fast Responder
                    </Badge>
                  )}
                  {badges.superhost && (
                    <Badge className="bg-yellow-400 text-yellow-900">
                      <Award className="h-3 w-3 mr-1" />
                      Superhost
                    </Badge>
                  )}
                </h1>
                <p className="text-purple-100">{t('accommodation.host.subtitle')}</p>
              </div>
            </div>
            <Button asChild className="bg-white text-purple-600 hover:bg-gray-100">
              <Link to="/host/listings/new">
                <Plus className="h-4 w-4 mr-2" />
                {t('accommodation.host.createListing')}
              </Link>
            </Button>
          </div>
          
          {/* Enhanced Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{stats.listings_count || 0}</p>
                <p className="text-sm text-purple-100">{t('accommodation.host.listings')}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{pendingBookings.length}</p>
                <p className="text-sm text-purple-100">{t('accommodation.host.pendingBookings')}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{stats.confirmed_30d || 0}</p>
                <p className="text-sm text-purple-100">{t('accommodation.host.confirmedBookings')}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">€{stats.earnings_30d || 0}</p>
                <p className="text-sm text-purple-100">{t('accommodation.host.earnings')} (30d)</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{stats.avg_quality_score || 0}</p>
                <p className="text-sm text-purple-100">Quality Score</p>
              </CardContent>
            </Card>
          </div>

          {/* Performance Metrics Row */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-3 flex items-center gap-3">
                <Clock className="h-5 w-5" />
                <div>
                  <p className="text-sm text-purple-100">Avg Response Time</p>
                  <p className="font-bold">
                    {stats.avg_response_time_minutes 
                      ? `${Math.round(stats.avg_response_time_minutes)} min` 
                      : 'N/A'}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-3 flex items-center gap-3">
                <TrendingUp className="h-5 w-5" />
                <div>
                  <p className="text-sm text-purple-100">Response Rate</p>
                  <p className="font-bold">{stats.response_rate || 0}%</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-3 flex items-center gap-3">
                <BarChart3 className="h-5 w-5" />
                <div>
                  <p className="text-sm text-purple-100">Conversion Rate</p>
                  <p className="font-bold">{stats.acceptance_rate || 0}%</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trial/Subscription Notice */}
          {subscription.status === 'trial' && subscription.trial_days_remaining > 0 && (
            <div className="mt-4 bg-white/10 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5" />
                <div>
                  <p className="font-medium">Free Trial Active</p>
                  <p className="text-sm text-purple-100">
                    {subscription.trial_days_remaining} days remaining
                  </p>
                </div>
              </div>
              <Button size="sm" className="bg-white text-purple-600 hover:bg-gray-100">
                Upgrade Now
              </Button>
            </div>
          )}
          
          {subscription.status === 'expired' && (
            <div className="mt-4 bg-red-500/20 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5" />
                <div>
                  <p className="font-medium">Trial Expired</p>
                  <p className="text-sm text-purple-100">
                    Subscribe to reactivate your listings
                  </p>
                </div>
              </div>
              <Button size="sm" className="bg-white text-purple-600 hover:bg-gray-100">
                Subscribe Now
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="bookings" className="relative">
              {t('accommodation.host.bookings')}
              {pendingBookings.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingBookings.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="listings">{t('accommodation.host.listings')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Quality Tips */}
            {listings.some(l => l.tips?.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                    Improve Your Listings
                  </CardTitle>
                  <CardDescription>
                    Follow these tips to get more bookings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {listings.filter(l => l.tips?.length > 0).slice(0, 3).map(listing => (
                      <div key={listing.listing_id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{listing.title}</span>
                          <QualityScoreBadge score={listing.quality_score} grade={listing.quality_grade} />
                        </div>
                        <ul className="space-y-1">
                          {listing.tips.map((tip, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1 h-1 bg-purple-500 rounded-full" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Conversion Funnel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  Booking Funnel (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Booking Requests</span>
                        <span className="font-medium">{dashboard?.conversion_funnel?.total_requests || 0}</span>
                      </div>
                      <Progress value={100} className="h-2" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Confirmed Bookings</span>
                        <span className="font-medium">{dashboard?.conversion_funnel?.confirmed || 0}</span>
                      </div>
                      <Progress 
                        value={dashboard?.conversion_funnel?.conversion_rate || 0} 
                        className="h-2 bg-gray-200" 
                      />
                    </div>
                    <span className="text-sm text-green-600 font-medium w-16">
                      {dashboard?.conversion_funnel?.conversion_rate || 0}%
                    </span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-600">€{stats.earnings_30d || 0}</p>
                    <p className="text-sm text-gray-500">Total Earnings</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{stats.avg_quality_score || 0}</p>
                    <p className="text-sm text-gray-500">Avg Quality Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/host/listings/new')}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Plus className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">Create Listing</p>
                    <p className="text-sm text-gray-500">Add a new property</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CalendarIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Update Calendar</p>
                    <p className="text-sm text-gray-500">Manage availability</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Update Pricing</p>
                    <p className="text-sm text-gray-500">Adjust your rates</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-6">
            {/* Pending Bookings */}
            {pendingBookings.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  {t('accommodation.host.pendingBookings')} ({pendingBookings.length})
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {pendingBookings.map(booking => (
                    <BookingCard 
                      key={booking.booking_id} 
                      booking={booking} 
                      onRespond={handleBookingResponse}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Confirmed Bookings */}
            {confirmedBookings.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  {t('accommodation.host.confirmedBookings')} ({confirmedBookings.length})
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {confirmedBookings.map(booking => (
                    <BookingCard 
                      key={booking.booking_id} 
                      booking={booking}
                      onRespond={handleBookingResponse}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            )}

            {bookings.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700">No bookings yet</h3>
                  <p className="text-gray-500 mt-1">
                    Bookings will appear here once guests request your listings.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings" className="space-y-4">
            {listings.length > 0 ? (
              listings.map(listing => (
                <ListingCard 
                  key={listing.listing_id} 
                  listing={listing}
                  onStatusChange={handleStatusChange}
                  t={t}
                />
              ))
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700">{t('accommodation.host.noListings')}</h3>
                  <p className="text-gray-500 mt-1">{t('accommodation.host.noListingsDesc')}</p>
                  <Button className="mt-4 bg-purple-600 hover:bg-purple-700" asChild>
                    <Link to="/host/listings/new">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('accommodation.host.createListing')}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HostDashboardPage;
